import { useEffect, useState } from "react";
import { ApiRequest, ShowLoader } from "GlobalFunctions";
import { ProjectConfig } from "Global";
import { Chip, Button } from "@mui/material";
import { PiPencilSimple } from "react-icons/pi";
import { PiCaretDown } from "react-icons/pi";

import BillingHistory from "./components/BillingHistory";
import CancelSubscription from "./components/CancelSubscription";
import UpdatePaymentMethod from "./components/UpdatePaymentMethod";
import UpdateShippingInfo from "./components/UpdateShippingInfo";
import ResumeSubscription from "./components/ResumeSubscription";

const PENNI_LOGO_URL = `${ProjectConfig.api_url}/assets/penni_logo_white.svg`;

const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric',
        timeZone: 'UTC'
    });
};

const getThemeColors = (primaryColor: string | undefined) => {
    // Si no hay color primario, usar valores por defecto
    if (!primaryColor) {
        return {
            primary: '#073508',
            secondary: '#E9F7BB80'
        };
    }

    // Normalizar el color primario a minúsculas
    const normalizedPrimary = primaryColor.toLowerCase();

    // Si el color primario es negro o muy oscuro, usar gris claro como secundario
    if (normalizedPrimary === '#000000' || normalizedPrimary === '#000' || normalizedPrimary === 'black') {
        return {
            primary: '#000000',
            secondary: '#E5E5E5'
        };
    }

    // Convertir el color primario a RGB si está en formato hex
    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    const rgb = hexToRgb(normalizedPrimary);
    if (rgb) {
        // Crear color secundario con 20% de opacidad
        const secondary = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`;
        return {
            primary: primaryColor,
            secondary: secondary
        };
    }

    // Si no se puede convertir, usar el color primario con opacidad CSS
    return {
        primary: primaryColor,
        secondary: `${primaryColor}33` // 33 en hex = ~20% opacidad
    };
};

const data = {
    store_name: "Demo Store",
}

export default function SubscriptionDetails(props: any) {

    const { subscriptionCode } = props;
    const [subscriptionData, setSubscriptionData] = useState<any>(null);
    const [showCancelView, setShowCancelView] = useState(false);
    const [showResumeView, setShowResumeView] = useState(false);
    const [showUpdateCardView, setShowUpdateCardView] = useState(false);
    const [showUpdateShippingView, setShowUpdateShippingView] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        ShowLoader(true);
        getSubscriptionDetails();
    }, [subscriptionCode]);

    const getSubscriptionDetails = async () => {
        await ApiRequest({
            url: "/subscriptions/getSubscriptionDetailsByCode",
            method: "GET",
            query: {
                subscription_code: subscriptionCode,
            },
            setResponse: async (response: any) => {
                ShowLoader(false);
                
                if (response.status === "success") {
                    setSubscriptionData(response.data);
                    console.log('Subscription details loaded:', response.data);
                } else {
                    console.error('Error loading subscription details:', response.message);
                }
            },
        });
    };

    const themeColors = getThemeColors(subscriptionData?.primary_color);

    return (
        <div className="pc-subscription-details">

            <div 
                className="pc-subscription-details-aside"
                style={{ backgroundColor: themeColors.primary }}
            >
                <div className="pc-subscription-details-aside-top">
                    <div className="pc-subscription-logo">
                        {subscriptionData?.logo_url ? (
                            <img src={subscriptionData.logo_url} alt="logo" />
                        ) : (
                            <div>logo</div>
                        )}
                    </div>
                    <div className="pc-subscription-details-aside-title">Manage your {data.store_name} subscriptions</div>
                </div>

                <div className="pc-subscription-details-powered">
                    Powered by <img src={PENNI_LOGO_URL} alt="Pennicart" className="pc-powered-logo" />
                </div>
            </div>

            <div className="pc-subscription-details-main">
                
                {showCancelView ? (
                    <CancelSubscription 
                        subscriptionData={subscriptionData}
                        onGoBack={() => setShowCancelView(false)}
                        onCancelSuccess={() => {
                            setShowCancelView(false);
                            getSubscriptionDetails();
                        }}
                    />
                ) : showResumeView ? (
                    <ResumeSubscription 
                        subscriptionData={subscriptionData}
                        onGoBack={() => setShowResumeView(false)}
                        onResumeSuccess={() => {
                            setShowResumeView(false);
                            getSubscriptionDetails();
                        }}
                    />
                ) : showUpdateCardView ? (
                    <UpdatePaymentMethod 
                        subscriptionData={subscriptionData}
                        onGoBack={() => setShowUpdateCardView(false)}
                        onUpdateSuccess={() => {
                            setShowUpdateCardView(false);
                            getSubscriptionDetails();
                        }}
                    />
                ) : showUpdateShippingView ? (
                    <UpdateShippingInfo 
                        subscriptionData={subscriptionData}
                        onGoBack={() => setShowUpdateShippingView(false)}
                        onUpdateSuccess={() => {
                            setShowUpdateShippingView(false);
                            getSubscriptionDetails();
                        }}
                    />
                ) : (
                    <>
                        <div className="pc-subscription-section">
                            <div className="pc-subscription-section-title">CURRENT SUBSCRIPTION</div>
                            
                            <div className="pc-subscription-section-content">
                                <div className="pc-subscription-left-section">
                                    <div className="pc-subscription-product-name">{subscriptionData?.products?.[0]?.product_name}</div>
                                    <div className="pc-subscription-price">USD${subscriptionData?.total_amount} {subscriptionData?.products?.[0]?.frequency?.toLowerCase()}</div>
                                    <div 
                                        className='pc-subscription-link' 
                                        onClick={() => setShowDetails(!showDetails)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {showDetails ? 'Hide details' : 'View details'} <PiCaretDown style={{ transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                                    </div>

                                    {/* Expanded Details Section */}
                                    {showDetails && (
                                        <div className="pc-subscription-details-expanded" style={{
                                            marginTop: '20px',
                                            padding: '15px',
                                            backgroundColor: '#f9fafb',
                                            borderRadius: '8px',
                                            fontSize: '14px'
                                        }}>
                                            {/* Product SKU */}
                                            {subscriptionData?.products?.[0]?.product_sku && (
                                                <div style={{ marginBottom: '10px' }}>
                                                    <strong>SKU:</strong> {subscriptionData.products[0].product_sku}
                                                </div>
                                            )}

                                            {/* Billing Cycle */}
                                            <div style={{ marginBottom: '10px' }}>
                                                <strong>Billing Cycle:</strong> {subscriptionData?.products?.[0]?.frequency}
                                            </div>

                                            {/* Price Breakdown */}
                                            <div style={{ 
                                                marginTop: '15px', 
                                                paddingTop: '15px', 
                                                borderTop: '1px solid #e5e7eb' 
                                            }}>
                                                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Subtotal:</span>
                                                    <span>USD${subscriptionData?.subtotal?.toFixed(2)}</span>
                                                </div>
                                                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Shipping ({subscriptionData?.products?.[0]?.shipping?.shipping_name}):</span>
                                                    <span>USD${subscriptionData?.shipping_price?.toFixed(2)}</span>
                                                </div>
                                                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Tax ({subscriptionData?.tax_rate}%):</span>
                                                    <span>USD${subscriptionData?.total_taxes?.toFixed(2)}</span>
                                                </div>
                                                <div style={{ 
                                                    marginTop: '10px', 
                                                    paddingTop: '10px', 
                                                    borderTop: '2px solid #e5e7eb',
                                                    display: 'flex', 
                                                    justifyContent: 'space-between',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px'
                                                }}>
                                                    <span>Total:</span>
                                                    <span>USD${subscriptionData?.total_amount?.toFixed(2)}</span>
                                                </div>
                                            </div>

                                            {/* Start Date */}
                                            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
                                                <strong>Subscription started:</strong> {formatDate(subscriptionData?.start_date)}
                                            </div>
                                        </div>
                                    )}

                                    {/* Subscription Status Message */}
                                    {subscriptionData?.show_cancellation_message ? (
                                        <p className="pc-subscription-renews pc-subscription-canceled">
                                            Subscription will be canceled on {formatDate(subscriptionData?.latest_next_payment_date)}.
                                        </p>
                                    ) : subscriptionData?.cancellation_completed ? (
                                        <p className="pc-subscription-renews pc-subscription-canceled">
                                            Subscription has been cancelled since {formatDate(subscriptionData?.latest_next_payment_date)}.
                                        </p>
                                    ) : subscriptionData?.next_payment_date && (
                                        <p className="pc-subscription-renews">
                                            Your subscription renews on {subscriptionData?.next_due_date}.
                                        </p>
                                    )}
                                </div>
                                <div className="pc-subscription-right-section">
                                    {subscriptionData?.status === 'active' && !subscriptionData?.show_cancellation_message && !subscriptionData?.cancellation_completed && (
                                        <Button
                                            className="pc-cancel-subscription-btn"
                                            onClick={() => setShowCancelView(true)}
                                            style={{
                                                backgroundColor: themeColors.secondary,
                                                color: themeColors.primary,
                                                borderRadius: '4px',
                                                padding: '10px 25px',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                border: 'none'
                                            }}
                                        >
                                            Cancel Subscription
                                        </Button>
                                    )}
                                    {subscriptionData?.show_cancellation_message && !subscriptionData?.cancellation_completed && (
                                        <Button
                                            className="pc-resume-subscription-btn"
                                            onClick={() => setShowResumeView(true)}
                                            style={{
                                                backgroundColor: themeColors.primary,
                                                color: '#FFFFFF',
                                                borderRadius: '4px',
                                                padding: '10px 25px',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                border: 'none'
                                            }}
                                        >
                                            Resume Subscription
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="pc-subscription-section">
                            <div className="pc-subscription-section-title">PAYMENT METHOD</div>
                            
                            <div className="pc-subscription-section-content">
                                <div className="pc-payment-method-content">
                                    <div className="pc-payment-method-info">
                                        <div className="pc-payment-card-row">
                                            <img 
                                                src={subscriptionData?.card_logo_url} 
                                                alt={subscriptionData?.payment_data?.ssl_card_short_description || 'Card'} 
                                                className="pc-card-logo"
                                            />
                                            <span className="pc-card-number">- {subscriptionData?.payment_data?.ssl_card_number?.slice(-4) || '****'}</span>
                                            {subscriptionData?.products?.[0]?.failed_attempts > 0 && (
                                                <Chip label="● Card Failed" style={{ backgroundColor: "#FFE5E5", color: "#B91C1C", borderRadius: "5px", marginLeft: "20px" }} />
                                            )}
                                        </div>
                                        <p className="pc-payment-email">{subscriptionData?.email}</p>
                                        <p className="pc-payment-address">
                                            {subscriptionData?.city_name ? `${subscriptionData.city_name}, ` : ''}{subscriptionData?.region_code} {subscriptionData?.postal_code} {subscriptionData?.country_code}
                                        </p>
                                    </div>
                                    <div className="pc-payment-actions">
                                        <a className="pc-subscription-link" onClick={() => setShowUpdateCardView(true)}><PiPencilSimple /> Update information</a>
                                        <span className="pc-more-options">•••</span>
                                    </div>
                                </div>
                                <div className="pc-payment-method-note">
                                    <Chip label="● Paid" style={{ backgroundColor: "#ADFFB2", color: "#417029", borderRadius: "5px" }} />
                                </div>
                            </div>
                        </div>

                        {/* Shipping Information */}
                        <div className="pc-subscription-section">
                            <div className="pc-subscription-section-title">SHIPPING INFORMATION</div>
                            
                            <div className="pc-shipping-info-content">
                                <p className="pc-shipping-name">{subscriptionData?.client_name} {subscriptionData?.client_lastname}</p>
                                <p className="pc-shipping-address">
                                    {subscriptionData?.city_name ? `${subscriptionData.city_name}, ` : ''}{subscriptionData?.region_code} {subscriptionData?.postal_code}
                                </p>
                                <p className="pc-shipping-country">{subscriptionData?.country_name}</p>
                                <p className="pc-shipping-phone">{subscriptionData?.phone}</p>
                                
                                <div className="pc-shipping-actions">
                                    <a className="pc-subscription-link" onClick={() => setShowUpdateShippingView(true)}><PiPencilSimple /> Update information</a>
                                </div>
                            </div>
                        </div>

                        {/* Billing History */}
                        <BillingHistory 
                            payments={subscriptionData?.payments} 
                            subscriptionId={subscriptionData?.subscription_id}
                            themeColors={themeColors}
                        />

                        {/* <pre style={{ 
                            background: '#f5f5f5', 
                            padding: '15px', 
                            borderRadius: '8px', 
                            overflow: 'auto',
                            maxHeight: '500px',
                            fontSize: '12px'
                        }}>
                            {JSON.stringify(subscriptionData, null, 2)}
                        </pre> */}
                    </>
                )}

            </div>

        </div>
    );
}